import { createContext, useState, useEffect, useCallback } from 'react';
import api, {
  setAccessToken,
  getAccessToken,
  refreshAccessToken,
  onAuthFailure,
} from '../utils/api';
import { resetAllIdentities, publishLogout, subscribeToLogout } from '../utils/identity';
import queryClient from '../utils/queryClient';

export const AuthContext = createContext(null);

const SKIP_SESSION_RESTORE = [
  /^\/admin\/login\/?$/,
  /^\/login\/?$/,
  /^\/admin\/forgot-password/,
  /^\/admin\/reset-password/,
  /^\/apply/,
  /^\/track/,
  /^\/careers/,
  /^\/candidate/,
  /^\/portal/,
  /^\/accept-invite/,
];

function shouldRestoreSession() {
  return !SKIP_SESSION_RESTORE.some((re) => re.test(window.location.pathname));
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // On mount: restore session. Access token is memory-only, so a full reload
  // must re-issue it via the httpOnly refresh cookie before calling /me.
  // Skip /me when there is no access token — the interceptor would refresh and
  // a second StrictMode refresh would rotate the cookie out from under the
  // first request.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!getAccessToken()) {
          if (!shouldRestoreSession()) {
            if (!cancelled) setUser(null);
            return;
          }
          await refreshAccessToken();
        }
        const res = await api.get('/auth/me');
        if (!cancelled) setUser(res.data.user);
      } catch {
        // Restore failed — treat it as no session at all and drop anything a
        // previous session left in the cache.
        resetAllIdentities();
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // A dead session discovered by axios (expired refresh, admin deactivated
  // mid-session) has to tear down the shell, or it keeps rendering cached
  // candidate PII while every request 401s.
  useEffect(() => onAuthFailure(() => {
    setUser(null);
    resetAllIdentities();
  }), []);

  // Signing out in another tab must sign out this one too: the token is
  // in-memory, so an untold tab stays usable for the whole token lifetime.
  useEffect(() => subscribeToLogout(() => {
    setUser(null);
    resetAllIdentities();
  }), []);

  const login = useCallback(async (email, password) => {
    const res = await api.post('/auth/login', { email, password });
    // Drop the previous admin's cached candidate PII before the new session can
    // read it out of cache.
    queryClient.clear();
    setAccessToken(res.data.accessToken);  // memory only — never localStorage
    setUser(res.data.user);
    return res.data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } catch {
      // ignore
    }
    setUser(null);
    // Clears the admin token, the candidate token and the whole query cache,
    // then tells the other tabs.
    resetAllIdentities();
    publishLogout();
  }, []);

  const hasRole = useCallback(
    (...roles) => user && roles.includes(user.role),
    [user]
  );

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    login,
    logout,
    hasRole,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
